var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { isNil, isString, useClasses, useConsoleLog, useControlledStateWarning, useLayoutProps, useProps, } from '@hawkins/helpers';
import { IconCheckboxIndeterminate } from '@hawkins/icons/lib/iconCheckboxIndeterminate';
import { IconCheckboxSelected } from '@hawkins/icons/lib/iconCheckboxSelected';
import { FormGroupContext } from '../formGroup';
import { useCombineRefs } from '../hooks';
import { Text } from '../text';
import { useCheckboxStyles } from './checkbox.styles';
import { CheckboxGroupContext } from './checkboxGroup.context';
var displayName = 'Checkbox';
/**
 * A checkbox can serve as a selector for multiple entities.
 */
export var Checkbox = React.forwardRef(function (props, ref) {
    var _a, _b, _c;
    var _d;
    // Props
    var _e = useProps(displayName, props), ariaLabel = _e["aria-label"], autoFocus = _e.autoFocus, checkedProp = _e.checked, classesProp = _e.classes, className = _e.className, _f = _e.compact, compact = _f === void 0 ? false : _f, component = _e.component, _g = _e.defaultChecked, defaultChecked = _g === void 0 ? false : _g, _h = _e.disabled, disabledProp = _h === void 0 ? false : _h, _j = _e.indeterminate, indeterminate = _j === void 0 ? false : _j, _k = _e.inputProps, inputProps = _k === void 0 ? {} : _k, inputRef = _e.inputRef, label = _e.label, labelProps = _e.labelProps, layout = _e.layout, name = _e.name, onChange = _e.onChange, readOnly = _e.readOnly, required = _e.required, styleProp = _e.style, tabIndex = _e.tabIndex, value = _e.value, restProps = __rest(_e, ['aria-label', "autoFocus", "checked", "classes", "className", "compact", "component", "defaultChecked", "disabled", "indeterminate", "inputProps", "inputRef", "label", "labelProps", "layout", "name", "onChange", "readOnly", "required", "style", "tabIndex", "value"]);
    useControlledStateWarning(checkedProp, { displayName: displayName, stringifiedNullValue: 'false' });
    var hasLabel = !isNil(label);
    var Component = component !== null && component !== void 0 ? component : (hasLabel ? 'label' : 'span');
    var internalCheckboxRef = React.useRef(null);
    var combinedCheckboxRef = useCombineRefs([inputRef, internalCheckboxRef]);
    // Context
    var groupContext = React.useContext(CheckboxGroupContext);
    // Read from the form group context, if it is inside a <FormGroup/>
    var formGroupContext = React.useContext(FormGroupContext);
    var contextOnChange = groupContext === null || groupContext === void 0 ? void 0 : groupContext.onChange;
    var contextValue = groupContext === null || groupContext === void 0 ? void 0 : groupContext.value;
    var disabled = (formGroupContext === null || formGroupContext === void 0 ? void 0 : formGroupContext.disabled) || disabledProp;
    var isInGroup = !!groupContext;
    var isCheckedControlled = !isNil(checkedProp) || isInGroup;
    useConsoleLog(isInGroup && !isString(value), 'A `Checkbox` inside a `CheckboxGroup` is missing `props.value`. This is required for the `CheckboxGroup` to work properly.', { displayName: displayName, docsPath: 'components-checkbox#group', level: 'error' });
    // State
    var _l = __read(React.useState(defaultChecked), 2), internalChecked = _l[0], setInternalChecked = _l[1];
    var checked = (_d = (isInGroup ? isString(value) && Array.isArray(contextValue) && contextValue.includes(value) : checkedProp)) !== null && _d !== void 0 ? _d : internalChecked;
    // Callbacks
    var inputOnChange = React.useCallback(function (ev) {
        var newChecked = ev.target.checked;
        if (!isCheckedControlled)
            setInternalChecked(newChecked);
        onChange === null || onChange === void 0 ? void 0 : onChange(ev, newChecked);
        contextOnChange === null || contextOnChange === void 0 ? void 0 : contextOnChange(ev);
    }, [contextOnChange, isCheckedControlled, onChange]);
    // Styles
    var baseClasses = useCheckboxStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var fakeCheckboxClassName = clsx(classes.fakeCheckbox, (_a = {},
        _a[classes.fakeCheckboxWithIcon] = checked || indeterminate,
        _a));
    var inputClassName = clsx(classes.input, (_b = {},
        _b[classes.inputDisabled] = disabled,
        _b[classes.inputEnabled] = !disabled,
        _b));
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_c = {},
        _c[classes.rootDisabled] = disabled,
        _c));
    // Update the indeterminate property when it changes
    React.useEffect(function () {
        if (internalCheckboxRef.current) {
            internalCheckboxRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);
    return (_jsxs(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: [_jsxs("span", __assign({ className: classes.checkboxContainer }, { children: [_jsx("input", __assign({ "aria-label": ariaLabel, autoFocus: autoFocus, checked: checked, className: inputClassName, disabled: disabled, name: name, onChange: inputOnChange, readOnly: readOnly, ref: combinedCheckboxRef, required: required, tabIndex: tabIndex, type: "checkbox", value: value }, inputProps)), _jsx("span", __assign({ className: fakeCheckboxClassName }, { children: (indeterminate && _jsx(IconCheckboxIndeterminate, { className: classes.icon, size: "small" })) ||
                            (checked && _jsx(IconCheckboxSelected, { className: classes.icon, size: "small" })) }))] })), hasLabel && (_jsx(Text, __assign({ className: classes.label, colorVariant: disabled ? 'disabled' : 'default', component: "span", variant: compact ? 'label' : 'body' }, labelProps, { children: label })))] })));
});
Checkbox.displayName = displayName;
